import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import loader from "../assets/loader.webp";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { toast } from "react-toastify";
import DeliveryTimeInput from "./DeliveryTimeInput";
import { formatDuration, formatReadyAt } from "../utility/duration";
import { formatNumbersWithDecimal } from "./formatNumbersWIthDecimal";

// Shared queue view for admin, manager and cashier. `api` is the role's axios
// instance; only admins get `canExtend`.
function QueuedOrders({
  api,
  role,
  canExtend = false,
  currency = "",
  bare = false,
}) {
  const queryClient = useQueryClient();
  const queryKey = ["queued-orders", role];
  const [extending, setExtending] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [extraSeconds, setExtraSeconds] = useState("");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showFilter, setShowFilter] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await api.get("orders/queued", {
        // The API is paginated (10 per page by default). Ask for everything
        // and search/filter in the browser: server-side `search` currently 500s.
        params: { sortOrder: "created_at", direction: "DESC", per_page: 1000 },
      });
      // Paginated: { data: { data: [...rows], total, last_page, ... } }.
      // Older/unpaginated responses were a plain array - accept both.
      const payload = res?.data?.data;
      return Array.isArray(payload) ? payload : payload?.data || [];
    },
    refetchInterval: 60000,
  });

  const closeExtend = () => {
    setExtending(null);
    setExtraSeconds("");
  };

  const handleExtend = async () => {
    if (!extraSeconds) {
      toast.error("Enter a time greater than 0.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`orders/queued/${extending.id}/extend`, {
        additional_seconds: extraSeconds,
      });
      toast.success("Delivery time extended.");
      closeExtend();
      queryClient.invalidateQueries({ queryKey });
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to extend delivery time.",
      );
    } finally {
      setSaving(false);
    }
  };

  const status = (readyAt) => {
    const diff = new Date(readyAt).getTime() - Date.now();
    if (Number.isNaN(diff)) return null;
    return diff <= 0
      ? { text: "Ready", cls: "bg-green-100 text-green-800" }
      : {
          text: `in ${formatDuration(diff / 1000)}`,
          cls: "bg-yellow-100 text-yellow-800",
        };
  };

  const statusFormat = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str;

  const term = search.trim().toLowerCase();
  const orders = (data || []).filter((q) => {
    // The backend keeps the queue row when an order is deleted (e.g. a draft);
    // skip those orphans so they don't show up as blank lines.
    if (!q.order) return false;
    if (
      statusFilter &&
      (q.order?.status || "").toLowerCase() !== statusFilter
    ) {
      return false;
    }
    if (!term) return true;
    const haystack = [
      q.order?.orderNO,
      q.order?.customer?.name,
      ...(q.order?.orderdetails || []).map((d) => d.product_name),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(term);
  });
  const columns = 7;

  return (
    <div
      className={
        bare
          ? "w-full"
          : "bg-white flex flex-col w-full lg:gap-3 items-center py-6 px-8"
      }
    >
      {/* Search and Filter */}
      <div className="flex flex-col lg:flex-row justify-between items-center w-full mb-2">
        <div className="flex items-center gap-2">
          <input
            type="text"
            className="border-2 border-gray-400 w-60 p-2 px-6"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
          />
        </div>
        <div className="relative">
          <button
            className={`ml-2 flex items-center gap-1 hover:cursor-pointer border-black border px-4 py-2 rounded ${
              statusFilter ? "bg-gray-200" : ""
            }`}
            onClick={() => setShowFilter(!showFilter)}
            aria-label="Filter"
            title={statusFilter ? `Filter: ${statusFilter}` : "Filter"}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="24"
              width="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-5 h-5"
            >
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="18" x2="20" y2="18" />
              <circle cx="8" cy="6" r="2" />
              <circle cx="16" cy="12" r="2" />
              <circle cx="8" cy="18" r="2" />
            </svg>
          </button>
          {showFilter && (
            <div className="absolute right-0 mt-1 z-10 bg-white border border-gray-300 rounded shadow-lg w-40 capitalize">
              {["", "completed", "pending", "draft"].map((value) => (
                <div
                  key={value || "all"}
                  onClick={() => {
                    setStatusFilter(value);
                    setShowFilter(false);
                  }}
                  className={`p-2 cursor-pointer ${
                    statusFilter === value ? "bg-blue-100" : "hover:bg-gray-100"
                  }`}
                >
                  {value || "all"}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="w-full flex justify-center items-center py-10">
          <img src={loader} alt="loader" className="w-10" />
        </div>
      ) : (
        <div className="w-full overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-300">
            <thead>
              <tr className="bg-gray-200">
                <th className="py-2 px-4 border-b border-gray-300 whitespace-nowrap">
                  ORDER NO
                </th>
                <th className="py-2 px-4 border-b border-gray-300 whitespace-nowrap">
                  CUSTOMER NAME
                </th>
                <th className="py-2 px-4 border-b border-gray-300 whitespace-nowrap">
                  TOTAL PRICE
                </th>
                <th className="py-2 px-4 border-b border-gray-300 whitespace-nowrap">
                  DELIVERY TIME
                </th>
                <th className="py-2 px-4 border-b border-gray-300 whitespace-nowrap">
                  READY IN
                </th>
                <th className="py-2 px-4 border-b border-gray-300 whitespace-nowrap">
                  STATUS
                </th>
                <th className="py-2 px-4 border-b border-gray-300 whitespace-nowrap">
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr>
                  <td colSpan={columns} className="p-4 text-center">
                    {data?.length
                      ? "No orders match your search."
                      : "The queue is empty."}
                  </td>
                </tr>
              )}
              {orders.map((q) => {
                const st = status(q.ready_at);
                return (
                  <tr
                    key={q.id}
                    className="hover:bg-gray-100 border-b border-gray-300"
                  >
                    <td className="py-2 px-4 text-wrap">{q.order?.orderNO}</td>
                    <td className="py-2 px-4 text-center">
                      {q.order?.customer?.name}
                    </td>
                    <td className="py-2 px-4 text-center">
                      {currency}
                      {formatNumbersWithDecimal(q.order?.total_price || 0)}
                    </td>
                    <td className="py-2 px-4 text-center">
                      {formatDuration(q.delivery_time)}
                    </td>
                    <td className="py-2 px-4 text-center">
                      {st && (
                        <span
                          className={`px-2 py-1 rounded-full text-xs whitespace-nowrap ${st.cls}`}
                        >
                          {st.text}
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-4 text-center">
                      <div
                        className={`p-1 text-white rounded-md ${
                          (q.order?.status || "").toUpperCase() === "COMPLETED"
                            ? "bg-green-500"
                            : "bg-yellow-500"
                        }`}
                      >
                        {statusFormat(q.order?.status)}
                      </div>
                    </td>
                    <td className="py-2 px-4">
                      <div className="flex justify-center items-center gap-3">
                        <VisibilityIcon
                          onClick={() => setViewing(q)}
                          className="text-yellow-400 cursor-pointer"
                          titleAccess="View items"
                        />
                        {canExtend && (
                          <button
                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded"
                            onClick={() => setExtending(q)}
                          >
                            Extend
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {viewing && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60">
          <div className="bg-white text-black shadow-2xl rounded-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto border border-gray-200">
            <div className="px-8 py-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                  <rect width="24" height="24" rx="4" fill="#1d4ed8" />
                  <path
                    d="M7 12.5V9a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v3.5"
                    stroke="#fff"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <circle cx="9" cy="16" r="1.5" fill="#fff" />
                  <circle cx="15" cy="16" r="1.5" fill="#fff" />
                </svg>
                Order Items
              </h3>
              <div className="overflow-auto rounded-md bg-white border border-gray-200">
                <table className="min-w-full text-[15px]">
                  <thead>
                    <tr className="bg-blue-50 text-blue-900">
                      <th className="py-2.5 px-4 font-medium border-b border-blue-200">
                        Product
                      </th>
                      <th className="py-2.5 px-4 font-medium border-b border-blue-200 text-center">
                        Quantity
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewing.order?.orderdetails || []).map((d, i) => (
                      <tr key={d.id || i} className="hover:bg-blue-50">
                        <td className="py-2.5 px-4">{d.product_name}</td>
                        <td className="py-2.5 px-4 text-center">
                          {d.quantity_ordered}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end mt-5">
                <button
                  onClick={() => setViewing(null)}
                  className="text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full shadow transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {extending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-semibold mb-1">Extend delivery time</h3>
            <p className="text-sm text-gray-600 mb-4">
              Order {extending.order?.orderNO} · currently{" "}
              {formatDuration(extending.delivery_time)}, ready at{" "}
              {formatReadyAt(extending.ready_at)}
            </p>
            <label className="block mb-1 text-sm font-medium">Add time</label>
            <DeliveryTimeInput seconds="" onChange={setExtraSeconds} />
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={closeExtend}
                className="px-4 py-2 border rounded"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleExtend}
                className="px-4 py-2 bg-darkblue text-white rounded"
                disabled={saving}
              >
                {saving ? "Saving..." : "Extend"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QueuedOrders;
